import { useState, useEffect } from "react";
import axios from "axios";
import { Star, GoogleLogo, ArrowSquareOut } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ n }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={16} weight="fill" className={i < Math.round(n) ? "text-[#F5A623]" : "text-[#D4D4D8]"} />
    ))}
  </div>
);

export const GoogleRatingBadge = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/google-reviews`).then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data?.configured || data.rating == null || !data.user_rating_count) return null;
  return (
    <a href={data.google_maps_uri || "https://www.google.com/maps"} target="_blank" rel="noreferrer" data-testid="google-rating-badge"
      className="mt-6 inline-flex items-center gap-3 bg-white/10 hover:bg-white/15 border border-white/15 rounded-sm px-4 py-2.5 transition-colors">
      <GoogleLogo size={22} weight="bold" className="text-white" />
      <span data-testid="google-rating-value" className="font-display font-black text-xl text-white">{Number(data.rating).toFixed(1)}</span>
      <Stars n={data.rating} />
      <span data-testid="google-rating-count" className="text-sm text-white/75">{data.user_rating_count} Google reviews</span>
    </a>
  );
};

export const GoogleReviewCards = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/google-reviews`).then(({ data }) => setData(data)).catch(() => {});
  }, []);
  const latest = (data?.reviews || []).slice(0, 3);
  if (!latest.length) return null;
  return (
    <div className="mt-6" data-testid="home-review-cards">
      <div className="grid md:grid-cols-3 gap-4">
        {latest.map((r, i) => (
          <a key={i} href={r.author_uri || data.google_maps_uri} target="_blank" rel="noreferrer" data-testid={`home-review-card-${i}`}
            className="bg-white rounded-lg p-4 flex flex-col gap-2 hover:-translate-y-0.5 transition-transform">
            <div className="flex items-center justify-between gap-2">
              <span className="font-display font-bold text-sm text-[#15306E] truncate">{r.author_name || "Google user"}</span>
              <Stars n={r.rating || 5} />
            </div>
            <p className="text-sm text-[#3F3F46] leading-relaxed line-clamp-3">{r.text}</p>
            <span className="text-xs text-[#71717A] flex items-center gap-1.5"><GoogleLogo size={12} weight="bold" /> {r.relative_time}</span>
          </a>
        ))}
      </div>
      <a href={data.google_maps_uri} target="_blank" rel="noreferrer" data-testid="home-review-cards-more" className="mt-3 inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white">
        Read all reviews on Google <ArrowSquareOut size={14} weight="bold" />
      </a>
    </div>
  );
};


export default function GoogleReviews() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/google-reviews`).then(({ data }) => setData(data)).catch(() => setData({ configured: false }));
  }, []);

  // Render nothing until we know it's configured and has reviews
  if (!data || !data.configured || !data.reviews?.length) return null;

  return (
    <section id="google-reviews" className="px-5 lg:px-8 py-20 lg:py-28 bg-white" data-testid="google-reviews-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 text-[#46A01E] font-display font-bold uppercase tracking-widest text-xs mb-4">
              <span className="h-px w-8 bg-[#46A01E]" /> Google Reviews
            </div>
            <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-[#15306E]">
              What San Diego says about us.
            </h2>
          </div>
          {data.rating != null && (
            <div className="flex items-center gap-3 bg-[#F4F4F5] rounded-sm px-5 py-3">
              <GoogleLogo size={28} weight="bold" className="text-[#15306E]" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-display font-black text-2xl text-[#15306E]">{Number(data.rating).toFixed(1)}</span>
                  <Stars n={data.rating} />
                </div>
                <div className="text-xs text-[#52525B]">{data.user_rating_count} Google ratings</div>
              </div>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.reviews.map((r, i) => (
            <div key={i} data-testid={`google-review-${i}`} className="border border-[#E4E4E7] rounded-sm p-5 flex flex-col">
              <div className="flex items-center gap-3 mb-3">
                {r.author_photo_uri ? (
                  <img src={r.author_photo_uri} alt={r.author_name || "Reviewer"} className="w-10 h-10 rounded-full" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#15306E] text-white flex items-center justify-center font-bold">{(r.author_name || "G")[0]}</div>
                )}
                <div>
                  <div className="font-display font-bold text-[#15306E] text-sm">{r.author_name || "Google user"}</div>
                  <div className="text-xs text-[#71717A]">{r.relative_time}</div>
                </div>
              </div>
              <Stars n={r.rating} />
              <p className="text-sm text-[#3F3F46] leading-relaxed mt-3 line-clamp-6">{r.text}</p>
            </div>
          ))}
        </div>

        {data.google_maps_uri && (
          <div className="mt-8">
            <a href={data.google_maps_uri} target="_blank" rel="noreferrer" data-testid="google-reviews-more" className="inline-flex items-center gap-2 text-[#15306E] font-semibold hover:text-[#46A01E] transition-colors">
              Read all reviews on Google <ArrowSquareOut size={16} weight="bold" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
